import isNil from "lodash/isNil";

import { useAppSelector } from "@/store/hooks";
import {
  selectAppointmentTypeLabel,
  selectPatientId,
  selectProviderType,
  selectRecurrence,
  selectReschedulingAppointmentData,
} from "@/store/reducers/booking";
import { appointmentRecurrenceToFrequency } from "@/utils/schedule.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";

interface UseBookingAvailabilityRequestParams {
  appointmentType: string;
  hostExternalId: string;
  isPatientAttending: boolean;
  patientExternalId: string;
  providerAttendeeExternalIds?: string[];
  recurrenceFrequency?: EventRecurrence;
  supportAttendeeExternalIds?: string[];
}

const getParamsFromBookingState = ({
  appointmentType,
  patientExternalId,
  providerAttendeeExternalIds,
  providerType,
  recurrenceFrequency,
}: {
  appointmentType?: string;
  patientExternalId?: string;
  providerAttendeeExternalIds?: string[];
  providerType?: BookingProviderType;
  recurrenceFrequency?: EventRecurrence;
}): undefined | UseBookingAvailabilityRequestParams => {
  if (
    isNil(appointmentType) ||
    isNil(providerType) ||
    !isNotNilNotEmpty(patientExternalId)
  ) {
    return undefined;
  }

  return {
    appointmentType,
    hostExternalId: providerType.providerExternalId,
    isPatientAttending: true,
    patientExternalId,
    providerAttendeeExternalIds,
    recurrenceFrequency,
  };
};

const getParamsFromReschedulingState = ({
  reschedulingAppointmentData,
}: {
  reschedulingAppointmentData?: AppointmentDetailResponse;
}): undefined | UseBookingAvailabilityRequestParams => {
  if (isNil(reschedulingAppointmentData)) {
    return undefined;
  }

  const {
    appointmentTypeLabel,
    host: { externalId: hostExternalId },
    isPatientAttending,
    patient,
    providerAttendees,
    recurrence,
    supportAttendees,
  } = reschedulingAppointmentData;

  const patientExternalId = patient.externalId;

  if (!isNotNilNotEmpty(patientExternalId)) {
    return undefined;
  }

  const providerAttendeeExternalIds = providerAttendees?.map(
    (attendee) => attendee.externalId,
  );

  const recurrenceFrequency = appointmentRecurrenceToFrequency(recurrence);

  return {
    appointmentType: appointmentTypeLabel,
    hostExternalId,
    isPatientAttending,
    patientExternalId,
    providerAttendeeExternalIds,
    recurrenceFrequency,
    supportAttendeeExternalIds: supportAttendees?.map(
      (attendee) => attendee.externalId,
    ),
  };
};

const useBookingAvailabilityRequestParams = ():
  | undefined
  | UseBookingAvailabilityRequestParams => {
  const appointmentType = useAppSelector(selectAppointmentTypeLabel);
  const providerType = useAppSelector(selectProviderType);
  const patientExternalId = useAppSelector(selectPatientId);
  const recurrence = useAppSelector(selectRecurrence);

  const reschedulingAppointmentData = useAppSelector(
    selectReschedulingAppointmentData,
  );

  if (reschedulingAppointmentData) {
    return getParamsFromReschedulingState({ reschedulingAppointmentData });
  }

  return getParamsFromBookingState({
    appointmentType,
    patientExternalId,
    providerType,
    recurrenceFrequency: recurrence,
  });
};

export { getParamsFromBookingState, getParamsFromReschedulingState };

export default useBookingAvailabilityRequestParams;
